import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory25() {
  const { lesson, course, accent } = findLesson("music-theory", "25");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT25-Relative-vs-Parallel-Animated.mp4"
        caption="Relative (same notes, new home) vs Parallel (same root, new notes)"
        hint="≈ 24 sec · watch first"
      />
      <Chapter number="25" eyebrow="Music Theory · Chapter Twenty-five" title="Two ways to see one set of scales">
        <Prose>
          <p>
            We've now met both lenses. <strong>Relative scales</strong> share the same notes
            but disagree about which note is home. <strong>Parallel scales</strong> share
            home but disagree about which notes belong to it. Every modal relationship in
            music is one of these two — and both are useful. The relative lens helps you
            <em> reuse</em> notes you already know in a new mood. The parallel lens helps
            you <em>colour</em> a fixed home with new notes.
          </p>
          <p>
            This chapter sets the two side by side — pulls them apart, names the
            differences, and gives you a working method for choosing between them.
          </p>
          <MTQuote>
            Relative scales have the same set of notes, but different intervals and a
            different key or a different root or a different ringleader, while parallel
            scales have a different set of notes because the root will be the same.
          </MTQuote>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/25a-relative-vs-parallel-comparison.png"
          caption="Relative vs Parallel — same notes, different roots = relative (C major and A natural minor). Same root, different notes = parallel (C major and C minor). The four columns tabulate Set of Notes / Intervals / Root / Key Signature for both views."
        />
      </Chapter>

      <Chapter eyebrow="The two lenses" title="A side-by-side definition">
        <Prose>
          <p>
            Both words describe a relationship between two scales. The question is what
            stays the same and what changes:
          </p>
        </Prose>

        <TheoryBox label="Relative vs. parallel — the one-line difference">
          <ul className="space-y-2 text-[14px]">
            <li>
              <strong>Relative</strong> — the two scales share the <em>same set of notes</em>,
              but use a <em>different root</em>. (Example: G major and E minor — both are
              all the white keys, but G is home in one and E in the other.)
            </li>
            <li>
              <strong>Parallel</strong> — the two scales share the <em>same root</em>, but
              use a <em>different set of notes</em>. (Example: G major and G minor — both
              call G "home", but G major has F♯, B and E natural while G minor has F, B♭
              and E♭.)
            </li>
          </ul>
        </TheoryBox>

        <Prose>
          <p>
            And here's the trivia question your teacher will ask: what do relative and
            parallel scales have in common? Both differ in their <strong>intervals</strong>{" "}
            from the parent scale. That's how the sound changes. If the intervals didn't
            differ, the two scales would be identical — and there'd be nothing to compare.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The same modes · two perspectives" title="C Dorian — both ways">
        <Prose>
          <p>
            Take the Dorian mode as a case study. There are two ways to think about it:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>
              <strong>Relatively:</strong> Dorian is the major scale starting on its
              <strong> 2nd degree</strong>. So D Dorian = the C major scale starting from D.
              Same seven notes as C major. New root: D.
            </li>
            <li>
              <strong>Parallelly:</strong> Dorian is a minor scale with a raised 6th. So C
              Dorian = C natural minor with the A♭ raised back to A. Same root as C minor.
              Different notes.
            </li>
          </ul>
          <p>
            Both descriptions are correct. They're describing the same scale through two
            different windows. Improvisers tend to think relatively ("D Dorian is just C
            major's notes — easy"), while composers tend to think parallelly ("C Dorian is
            C minor with a brighter 6 — that brightness is exactly what I want here"). Which
            window you reach for depends on the question you're asking.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Two scale strips · two stories" title="Hear them next to each other">
        <Prose>
          <p>
            Below are two views. The first holds C as the root and runs through every
            parallel mode — same starting note, completely different notes around it. The
            second is the C major scale on its own — and you can hear the relatives by
            mentally starting from different notes within it (D Dorian, E Phrygian, F
            Lydian, G Mixolydian, A minor, B Locrian).
          </p>
        </Prose>

        <div className="mx-auto w-full max-w-5xl px-4 lg:px-8">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400 mb-1">
            Parallel — same root C, different modes
          </div>
        </div>
        <ScaleExplorer tonic="C" scales={["major", "dorian", "phrygian", "lydian", "mixolydian", "naturalMinor", "locrian"]} />

        <div className="mx-auto w-full max-w-5xl px-4 lg:px-8 pt-6">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400 mb-1">
            Relative — same notes (C major), different starting points
          </div>
        </div>
        <ScaleExplorer tonic="C" scales={["major"]} />

        <Prose>
          <p>
            For the relative view, mentally start the C major strip on each successive
            degree: starting on D gives D Dorian, starting on E gives E Phrygian, and so
            on. The notes never change. Only the home does.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="When to use which lens" title="Practical guide">
        <Prose>
          <p>
            Both lenses are useful, but for different jobs:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>
              <strong>Use relative</strong> when you already know a parent scale and want
              to play in one of its modes. ("I know C major. D Dorian is just C major
              starting on D — I don't need to learn anything new.") This is how
              improvisers and pianists tend to think.
            </li>
            <li>
              <strong>Use parallel</strong> when your <em>home note is fixed</em> and you
              want to compare moods. ("My song is in G. I want to know what G Lydian sounds
              like vs G major.") This is how composers and arrangers tend to think when
              borrowing chords.
            </li>
          </ul>
          <p>
            And both lenses produce the same result. C Dorian (parallel) and the second
            mode of B♭ major (relative) are the same scale. The two views are a translation
            problem — and learning to translate fluently between them is one of the most
            powerful tools a working musician can have.
          </p>
        </Prose>

        <Glossary>
          <Term name="Relative scale" def="Same notes, new root. G major and E minor are relatives." />
          <Term name="Parallel scale" def="Same root, different notes. G major and G minor are parallels." />
          <Term name="Modal interchange" def="The composer's name for borrowing chords from a parallel mode." />
          <Term name="Parent scale" def="The scale you derive a mode from. Every mode has a parent — and the relative-vs-parallel choice is just which root of that parent you call home." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="Songwriting in two dimensions" title="Combining both moves in one song">
        <Prose>
          <p>
            The richest songs use both lenses at once. Imagine writing a song in C major.
            You can:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>Move <strong>relatively</strong> to A minor for a darker section (same notes, new home).</li>
            <li>Move <strong>parallelly</strong> from A minor to A major (or A Mixolydian) to brighten that minor section without leaving its tonic.</li>
            <li>Borrow a single chord from C's <strong>parallel</strong> minor (E♭, A♭, B♭) for a flash of darkness inside the major.</li>
            <li>Resolve back to C major for the chorus.</li>
          </ul>
          <p>
            Each of those moves uses one of the two lenses. The whole journey is{" "}
            <em>tonal</em> — every chord makes sense — but the emotional arc covers four
            different colours. That's the kind of writing the relative-vs-parallel
            distinction unlocks.
          </p>
        </Prose>

        <MTQuote>
          It's quite, quite amazing actually how the same notes but different intervals just
          make you create different art.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="What's next" title="Putting modes into chords">
        <Prose>
          <p>
            With relative and parallel both in hand, the next chapter returns to harmony —
            and asks how the chord trees we built earlier shift when we move from major to
            its modes. Each mode generates its own diatonic chord tree, and those trees are
            the working palette of every modal song.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/25b-relative-parallel-network.png"
          caption="The relative/parallel network — C major ↔ A natural minor (relative); C major ↔ C natural minor (parallel); C natural minor ↔ E♭ major (relative); A natural minor ↔ A major (parallel). Walk the lattice and any scale connects to any other in two moves."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-26"
        label="Chapter 26 · Chord Trees - Chromatic (Part 1)"
        why="With relative and parallel both mapped, we build chord trees — the chromatic approach first, showing how any single melody note can be harmonised with six different triads."
      />
    </LessonShell>
  );
}
